import { Building2, Mail, MapPin, Phone } from "lucide-react";
import { Badge } from "@/components/ui";
import { TableBadge } from "@/components/ui/TableBadge/TableBadge";
import type { FirmReferralDirectoryEntry } from "@/lib/data/requests/firms/referral-profile";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";

export function ReferralFirmIdentity({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-xl border">
        <Building2 className="size-5" aria-hidden="true" />
      </div>
      <span className="min-w-0 leading-5 font-semibold [overflow-wrap:anywhere]">
        {name}
      </span>
    </div>
  );
}

export function ReferralPracticeAreas({
  areas,
}: {
  areas: FirmReferralDirectoryEntry["practiceAreas"];
}) {
  return areas.length ? (
    <div className="flex flex-wrap gap-1.5">
      {areas.map((area) => (
        <TableBadge
          key={area.id}
          className="bg-muted/50 h-auto text-left whitespace-normal"
        >
          {area.label}
        </TableBadge>
      ))}
    </div>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

export function ReferralServiceStates({ states }: { states: string[] }) {
  return states.length ? (
    <div className="flex items-start gap-2">
      <MapPin
        className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
        aria-hidden="true"
      />
      <span>{states.join(", ")}</span>
    </div>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

export function ReferralContactDetails({
  firm,
}: {
  firm: FirmReferralDirectoryEntry;
}) {
  return (
    <div className="space-y-1.5 text-sm">
      {firm.email && (
        <div className="flex items-center gap-2">
          <Mail
            className="text-muted-foreground size-3.5 shrink-0"
            aria-hidden="true"
          />
          <span className="[overflow-wrap:anywhere] break-words">
            {firm.email}
          </span>
        </div>
      )}
      {firm.phoneNumber && (
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          <Phone className="size-3.5 shrink-0" aria-hidden="true" />
          <span>{formatLocalOrIntlPhoneNumber(firm.phoneNumber)}</span>
        </div>
      )}
      {!firm.email && !firm.phoneNumber && (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}

export function ReferralConnectionBadge({
  status,
}: {
  status: "partner" | "incoming" | "outgoing";
}) {
  return (
    <Badge
      variant={
        status === "partner"
          ? "green"
          : status === "incoming"
            ? "blue"
            : "amber"
      }
      className="w-fit whitespace-nowrap"
    >
      {status === "partner"
        ? "Partner"
        : status === "incoming"
          ? "Incoming request"
          : "Request sent"}
    </Badge>
  );
}
